import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Alert,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Cancel as CancelIcon, Home as HomeIcon } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import paymentService from "../services/paymentService";

// Colores corporativos
const colors = {
  primary: "#00E400",
  primaryHover: "#00CC00",
  secondary: "#000000",
  background: "#F5F5F5",
  white: "#FFFFFF",
  textDark: "#000000",
  textLight: "#666666",
  success: "#00E400",
  warning: "#FFC107",
  error: "#DC3545",
};

const PagoCancelado = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { id } = useParams();
  const [pagoInfo, setPagoInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusSent, setStatusSent] = useState(false);

  const updateFacturaStatus = (facturaId, newStatus, paymentInfo = {}) => {
    try {
      const saved = localStorage.getItem("facturasGoPass");
      if (saved) {
        const facturas = JSON.parse(saved);
        const facturaIndex = facturas.findIndex((f) => f.id === facturaId);
        if (facturaIndex !== -1) {
          facturas[facturaIndex] = {
            ...facturas[facturaIndex],
            estado: newStatus,
            paymentCancelled: true,
            paymentCancelledAt: new Date().toISOString(),
            ...paymentInfo,
          };
          localStorage.setItem("facturasGoPass", JSON.stringify(facturas));
        }
      }
    } catch (error) {
      console.error("Error actualizando estado de factura:", error);
    }
  };

  const getStatusDetailMessage = (statusDetail) => {
    const messages = {
      REJECTED_BY_FRAUD: "Rechazado por control de fraude",
      REJECTED_BY_BANK: "Rechazado por el banco",
      REJECTED_BY_ISSUER: "Rechazado por el emisor de la tarjeta",
      INSUFFICIENT_FUNDS: "Fondos insuficientes",
      EXPIRED_CARD: "Tarjeta vencida",
      INVALID_CARD: "Tarjeta inválida",
      RESTRICTED_CARD: "Tarjeta restringida",
      TIMEOUT: "Tiempo de espera agotado",
      CANCELLED_BY_USER: "Cancelado por el usuario",
      SYSTEM_ERROR: "Error del sistema",
    };
    return messages[statusDetail] || statusDetail || "No especificado";
  };

  useEffect(() => {
    const loadTransactionData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (id && !statusSent) {
          await paymentService.cancelPayment(id);
          setStatusSent(true);
        }

        const response = await paymentService.getTransactionById(id);

        let monto = 0;
        let empresa = "No disponible";
        let nit = "No disponible";
        let facturaNumero = "No disponible";
        let transactionId = id;
        let paymentStatus = "cancelado";
        let statusDetail = null;
        let fecha = new Date().toLocaleString("es-CO");

        if (response && response.code === 0 && response.data) {
          const transaction = response.data;

          if (transaction.bill_number) facturaNumero = transaction.bill_number;
          if (transaction.company) empresa = transaction.company;
          if (transaction.nit) nit = transaction.nit;
          if (transaction.createdAt)
            fecha = new Date(transaction.createdAt).toLocaleString("es-CO");
          if (transaction.status) paymentStatus = transaction.status;

          if (transaction.metadata) {
            const metadata = transaction.metadata;
            if (metadata.data?.amount?.value) {
              monto = parseFloat(metadata.data.amount.value);
            }
            if (metadata.data?.customer) {
              const customer = metadata.data.customer;
              if (customer.name && customer.name !== "not-fount")
                empresa = customer.name;
              if (customer.nit) nit = customer.nit;
              if (customer.factura && customer.factura !== "")
                facturaNumero = customer.factura;
            }
          }

          if (transaction.more) {
            const more = transaction.more;

            if (more.link?.data) {
              const linkData = more.link.data;
              if (monto === 0 && linkData.amount?.value) {
                monto = parseFloat(linkData.amount.value);
              }
              if (linkData.customer) {
                if (
                  empresa === "No disponible" &&
                  linkData.customer.name &&
                  linkData.customer.name !== "not-fount"
                ) {
                  empresa = linkData.customer.name;
                }
                if (nit === "No disponible" && linkData.customer.nit) {
                  nit = linkData.customer.nit;
                }
                if (
                  facturaNumero === "No disponible" &&
                  linkData.customer.factura &&
                  linkData.customer.factura !== ""
                ) {
                  facturaNumero = linkData.customer.factura;
                }
              }
            }

            if (
              more.payment &&
              more.payment.data &&
              more.payment.data.length > 0
            ) {
              const paymentData = more.payment.data[0];
              if (paymentData.payment_id || paymentData.id) {
                transactionId = paymentData.payment_id || paymentData.id;
              }
              if (paymentData.status) paymentStatus = paymentData.status;
              if (paymentData.status_detail)
                statusDetail = paymentData.status_detail;
              if (monto === 0 && paymentData.amount)
                monto = parseFloat(paymentData.amount);
            }
          }

          const saved = localStorage.getItem("facturasGoPass");
          if (saved) {
            const facturas = JSON.parse(saved);
            const facturaEncontrada = facturas.find(
              (f) =>
                f.numeroFactura === facturaNumero ||
                f.paymentId === transactionId ||
                f.id.toString() === id,
            );
            if (facturaEncontrada) {
              updateFacturaStatus(facturaEncontrada.id, "cancelado", {
                paymentId: transactionId,
                paymentCancelledAt: new Date().toISOString(),
                montoIntentado: monto,
                statusDetail: statusDetail,
              });
            }
          }
        }

        setPagoInfo({
          monto,
          facturaNumero,
          empresa,
          nit,
          fecha,
          transactionId,
          status: paymentStatus,
          statusDetail,
          statusDetailMessage: getStatusDetailMessage(statusDetail),
        });
      } catch (err) {
        console.error("Error:", err);
        setError("Error al cargar los datos de la transacción");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadTransactionData();
    } else {
      setError("No se encontró ID de transacción");
      setLoading(false);
    }
  }, [id, statusSent]);

  const handleReintentar = () => {
    if (id) {
      localStorage.setItem("failed_payment_id", id);
    }
    navigate("/");
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: colors.background,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: colors.primary }} />
        <Typography sx={{ color: colors.textLight }}>
          Cargando información de la transacción...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: colors.background,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: colors.background,
        py: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Container maxWidth="md">
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            background: colors.white,
            overflow: "hidden",
          }}
        >
          {/* Header con logo y título principal */}
          <Box
            sx={{
              p: { xs: 3, sm: 4 },
              textAlign: "center",
              background: colors.white,
              borderBottom: `2px solid ${colors.error}`,
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src="/img/logohorinzontal.png"
              alt="GoPass"
              sx={{
                height: { xs: 45, sm: 65 },
                width: "auto",
                objectFit: "contain",
                mb: 3,
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />

            {/* Título principal */}
            <Typography
              variant={isMobile ? "h5" : "h4"}
              gutterBottom
              sx={{
                color: colors.error,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              Pago Cancelado
            </Typography>

            {/* Subtítulo */}
            <Typography
              variant="body1"
              sx={{
                color: colors.textLight,
                mb: 2,
              }}
            >
              La transacción no se completó correctamente
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Alert
              severity="warning"
              sx={{
                mb: 3,
                bgcolor: "#fff3cd",
                border: "1px solid #ffeeba",
                "& .MuiAlert-icon": { color: colors.warning },
              }}
            >
              <Typography variant="body2" sx={{ color: "#856404" }}>
                El proceso de pago fue cancelado antes de completarse. Ningún
                cargo ha sido realizado a su cuenta.
              </Typography>
            </Alert>

            <Typography
              variant="h6"
              sx={{ color: colors.secondary, mb: 2, fontWeight: "bold" }}
            >
              ¿Qué pudo haber sucedido?
            </Typography>

            <Paper
              sx={{
                p: { xs: 2, sm: 3 },
                bgcolor: colors.background,
                borderRadius: 2,
                mb: 3,
                border: `1px solid ${colors.primary}30`,
              }}
            >
              <Box component="ul" sx={{ color: colors.textDark, pl: 2, m: 0 }}>
                <li>Cancelaste voluntariamente el proceso de pago</li>
                <li>Hubo un error de conexión con la pasarela de pagos</li>
                <li>El tiempo de sesión expiró</li>
                <li>Los datos ingresados no fueron válidos</li>
                <li>Tu banco rechazó la transacción</li>
                {pagoInfo?.statusDetail && (
                  <li>
                    <strong>Motivo específico:</strong>{" "}
                    <span style={{ color: colors.error, fontWeight: "bold" }}>
                      {pagoInfo.statusDetailMessage}
                    </span>
                  </li>
                )}
              </Box>
            </Paper>

            <Typography
              variant="h6"
              sx={{ color: colors.secondary, mb: 2, fontWeight: "bold" }}
            >
              Información del Intento
            </Typography>
            <Paper
              sx={{
                p: { xs: 2, sm: 3 },
                bgcolor: colors.background,
                borderRadius: 2,
                mb: 3,
                border: `1px solid ${colors.primary}30`,
              }}
            >
              <Box sx={{ color: colors.secondary }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>ID de Transacción:</strong>{" "}
                  {pagoInfo?.transactionId || id || "No disponible"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Factura:</strong>{" "}
                  {pagoInfo?.facturaNumero || "No disponible"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Monto:</strong>{" "}
                  <span style={{ color: colors.error, fontWeight: "bold" }}>
                    ${(pagoInfo?.monto || 0).toLocaleString("es-CO")} COP
                  </span>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Empresa/Usuario:</strong>{" "}
                  {pagoInfo?.empresa || "No especificada"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>NIT/Identificación:</strong>{" "}
                  {pagoInfo?.nit || "No especificada"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Fecha del intento:</strong>{" "}
                  {pagoInfo?.fecha || new Date().toLocaleString("es-CO")}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Estado:</strong>{" "}
                  <span style={{ color: colors.error, fontWeight: "bold" }}>
                    {pagoInfo?.status || "Cancelado"}
                  </span>
                </Typography>
                {pagoInfo?.statusDetail && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Motivo del rechazo:</strong>{" "}
                    <span style={{ color: colors.error, fontWeight: "bold" }}>
                      {pagoInfo.statusDetailMessage}
                    </span>
                  </Typography>
                )}
              </Box>
            </Paper>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
                mt: 2,
              }}
            >
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                onClick={handleReintentar}
                sx={{
                  bgcolor: colors.primary,
                  textTransform: "none",
                  color: colors.secondary,
                  fontWeight: "bold",
                  px: 4,
                  py: 1.2,
                  "&:hover": { bgcolor: colors.primaryHover },
                }}
              >
                Intentar Nuevamente
              </Button>
            </Box>

            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                mt: 3,
                color: colors.textLight,
              }}
            >
              ¿Necesitas ayuda? Contáctanos a través de nuestro chat en línea o
              al correo soporte@gopass.com
            </Typography>
          </CardContent>
        </Card>

        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="caption" sx={{ color: colors.textLight }}>
            Pagos Automaticos de Colombia - Portal de Recaudos | Transacción no
            completada
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PagoCancelado;
